/**
 * UI-6b Task 11 — ChannelMix widget renderer.
 *
 * Phase 1 status: PLACEHOLDER. The intended backing data source is a
 * channel-grouped roll-up (voice / sms / webchat / etc.) of historical
 * calls. The current `/reports/call-volume` endpoint only supports
 * groupBy = hour | day | week — there is no `groupBy=channel`. Adding it
 * is tracked under UI-6d (filter/grouping expansion).
 *
 * Rather than fetch nothing and render an empty doughnut (misleading) or
 * fabricate data (worse), this Renderer surfaces a clear placeholder that
 * still demonstrates the loading + error code paths the real
 * implementation will need. When UI-6d ships `groupBy=channel`, swap the
 * placeholder branch for a PrimeReact Chart (type='doughnut').
 *
 * The Renderer still issues an HTTP call to `/reports/call-volume` so
 * that:
 *   - loading + error states are exercised end-to-end (not totally inert)
 *   - the network panel matches what the eventual Phase 2 version will do
 *   - if the endpoint ever returns the channel grouping, swapping the
 *     placeholder for a chart is a localized change
 */
import { useEffect, useState, type JSX } from 'react'
import { api } from '../../../../api/client.js'

export type ChannelMixConfig = Record<string, never>

interface CallVolumeRow {
  period: string
  count: number | string
}

interface CallVolumeResponse {
  rows: CallVolumeRow[]
}

function buildRange(days: number): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

export function ChannelMixRenderer(_props: { config: ChannelMixConfig }): JSX.Element {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [hasRows, setHasRows] = useState<boolean>(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const { from, to } = buildRange(7)
    const query = new URLSearchParams({ from, to, groupBy: 'day' })

    api
      .get<CallVolumeResponse>(`/reports/call-volume?${query.toString()}`)
      .then((resp) => {
        if (!cancelled) setHasRows((resp.rows ?? []).length > 0)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load call volume')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return (): void => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div data-testid="channel-mix-loading" className="text-sm text-gray-500">
        Loading channel mix…
      </div>
    )
  }
  if (error) {
    return (
      <div data-testid="channel-mix-error" className="text-sm text-red-600">
        {error}
      </div>
    )
  }

  // Phase 1: deliberate placeholder card. `hasRows` is shown so the user
  // knows the underlying call-volume feed is alive even though the channel
  // breakdown isn't yet supported.
  return (
    <div
      data-testid="channel-mix-placeholder"
      className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center"
    >
      <div className="text-sm font-semibold text-gray-700">Channel mix</div>
      <p className="max-w-xs text-xs text-gray-500">
        Channel breakdown awaiting backend filtering support (UI-6d). Underlying
        call-volume feed is {hasRows ? 'live' : 'empty'} for the last 7 days.
      </p>
    </div>
  )
}
